import { cn } from "@au-schein-de/au-ui-library";
import type { FormatedProductPrice } from "@dransay/api";
import { getProductsFilterSchema } from "@dransay/api/models";
import { zodResolver } from "@hookform/resolvers/zod";
import isEqual from "lodash/isEqual";
import { useQueryState } from "nuqs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFetcher, useLocation } from "react-router";
import { Snackbar } from "~/components/snackbar/snackbar.component";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { calculateProductsComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { ProductFilters as ProductFiltersV1 } from "~/features/products/filters/product-filters-v1.component";
import { useAutoRefreshProducts } from "~/hooks/use-auto-refresh-products.hook";
import { usePersistProductsUrl } from "~/hooks/use-back-to-shopping-url.hook";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";
import { useDeepCompareEffect } from "~/hooks/use-deep-compare-effect";
import { useMarkFavorites } from "~/hooks/use-mark-favorites.hook";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { productClickedEvent } from "~/tooling/telemetry/events";
import { getProductDetailsUrl } from "~/utils/routes";
import { ProductCard } from "../card/product-card.component";
import { CategoryPills } from "../categories/category-pills.component";
import { FILTER_REQUEST_DEBOUNCE_MS } from "../filters/constants";
import { ProductFilters } from "../filters/product-filters.component";
import { DeliveryVendorFilter } from "../filters/sections/delivery-vendor-filter.component";
import type {
	FacetCounts,
	FiltersConfig,
	FiltersState,
} from "../filters/types";
import { getTopProducerIds, useFilters } from "../filters/use-filters.hook";
import { filterSchemaParser } from "../filters/utils";
import { ProductList } from "../product-list.component";
import { ProductListVendorNavigation } from "../product-list-vendor-navigation.component";
import { useFilterWarnings } from "./use-filter-warnings.hook";

const badges = ["exclusive" as const];

type ProductsLayoutProps = {
	products: FormatedProductPrice[];
	count: number;
	totalCount?: number;
	filterConfig: FiltersConfig;
	useFiltersV2: boolean;
	enableProductsAutoRefresh: boolean;
	facets?: FacetCounts;
};

export function ProductsLayout({
	products,
	count,
	totalCount,
	filterConfig,
	useFiltersV2,
	enableProductsAutoRefresh,
	facets,
}: ProductsLayoutProps) {
	const { t } = useTranslation("product");
	const { isFavorite } = useMarkFavorites();

	const [filtersQuery, setFiltersQuery] = useQueryState(
		"filters",
		filterSchemaParser,
	);

	const initialValues: FiltersState = useMemo(() => {
		// Clone — `filtersQuery` is owned by nuqs and shared across hooks.
		// Mutating it caused the form to expose `producers: [...]` while the
		// URL still serialized to `{topProducersLowPrice:true}`, oscillating
		// downstream effects (facets fetcher, filter chips, count fetcher).
		const initialFilters: FiltersState = { ...(filtersQuery ?? {}) };

		if (initialFilters.topProducersLowPrice) {
			initialFilters.producers = getTopProducerIds(filterConfig);
		}

		return initialFilters;
	}, [filtersQuery, filterConfig]);

	const form = useForm({
		defaultValues: initialValues,
		resolver: zodResolver(getProductsFilterSchema()),
	});

	const {
		filters,
		setFilter,
		setBulkFilters,
		removeFilter,
		clearAllFilters: clearAllFiltersBase,
	} = useFilters(form, true);

	const {
		vendor,
		previousVendor,
		restorePreviousSelection,
		selectedVendorId,
		selectedDeliveryMethodKind,
		isCartVendor,
		setSelectedVendor,
	} = useVendorSelection();

	const location = useLocation();
	const serializedFiltersForPersistence = useMemo(
		() => filterSchemaParser.serialize(filters),
		[filters],
	);
	usePersistProductsUrl(serializedFiltersForPersistence);
	const [searchQuery] = useQueryState("search");
	const [isPharmacySelectorOpen, setIsPharmacySelectorOpen] = useState(false);

	// Fetcher for facet counts - updates when filters change
	const facetsFetcher = useFetcher<{ facets?: FacetCounts }>();
	const facetsFetcherRef = useRef(facetsFetcher);
	facetsFetcherRef.current = facetsFetcher;

	// Skip the initial facets fetch — SSR loader already provides facets.
	// Only fetch when filters/vendor/search actually change after hydration.
	const isInitialFacetsFetch = useRef(true);
	const loadFacets = useDebouncedCallback(
		(url: string) => facetsFetcherRef.current.load(url),
		{ delay: FILTER_REQUEST_DEBOUNCE_MS, leading: false },
	);
	useDeepCompareEffect(() => {
		if (isInitialFacetsFetch.current) {
			isInitialFacetsFetch.current = false;
			return;
		}

		const searchParams = new URLSearchParams();
		searchParams.set("pageSize", "1");
		searchParams.set("page", "0");

		if (selectedVendorId) {
			searchParams.set("vendorId", selectedVendorId);
		}
		if (selectedDeliveryMethodKind) {
			searchParams.set("deliveryMethod", selectedDeliveryMethodKind);
		}

		const serializedFilters = filterSchemaParser.serialize(filters);
		if (serializedFilters) {
			searchParams.set("filters", serializedFilters);
		}

		// Include search query to ensure facets match the product list
		if (searchQuery) {
			searchParams.set("search", searchQuery);
		}

		loadFacets(`/api/products?${searchParams.toString()}`);
	}, [filters, selectedVendorId, selectedDeliveryMethodKind, searchQuery]);

	// Use fetched facets if available, otherwise fall back to initial facets from loader
	const currentFacets = facetsFetcher.data?.facets ?? facets;

	const [_noRedirectQuery, setNoRedirectQuery] = useQueryState("noredirect");

	const debouncedSetFiltersQuery = useDebouncedCallback(
		() => {
			if (isEqual(filters, filtersQuery)) {
				return;
			}
			const areFiltersEmpty = Object.values(filters).every(
				(v) => v === undefined,
			);

			if (areFiltersEmpty) {
				setFiltersQuery(null);
				setNoRedirectQuery("true");
				return;
			}

			setFiltersQuery(filters);
		},
		{ delay: 200, trailing: true },
	);

	const clearAllFilters = useCallback(() => {
		debouncedSetFiltersQuery.cancel();
		clearAllFiltersBase();
		setFiltersQuery(null);
		setNoRedirectQuery("true");
	}, [
		clearAllFiltersBase,
		debouncedSetFiltersQuery,
		setFiltersQuery,
		setNoRedirectQuery,
	]);

	// Skip the initial filter sync — SSR already applied correct filters from URL.
	// Only sync when filters change from user interaction after hydration.
	const isInitialFilterSync = useRef(true);
	useDeepCompareEffect(() => {
		if (isInitialFilterSync.current) {
			isInitialFilterSync.current = false;
			return;
		}
		debouncedSetFiltersQuery();
	}, [filters]);

	const getProductListItemUrl = useCallback(
		(item: FormatedProductPrice) => {
			const urlBase = getProductDetailsUrl(item.id, item.name, item.kind);
			const queryParams = new URLSearchParams(location.search);
			queryParams.delete("search");
			queryParams.set("filters", filterSchemaParser.serialize(filters));

			const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";

			return `${urlBase}${qs}`;
		},
		[location.search, filters],
	);

	const getPriceComparisonDiscount = useCallback(
		(item: FormatedProductPrice) => {
			return calculateProductsComparisonDiscount(
				item.priceComparisonSet?.original,
				item.priceComparisonSet?.comparison,
			);
		},
		[],
	);

	const currency = useCurrency();

	useAutoRefreshProducts({
		enabled: enableProductsAutoRefresh,
	});

	const filterWarnings = useFilterWarnings({
		filterConfig,
		setFilter,
		selectedVendorId,
		selectedDeliveryMethodKind,
		setSelectedVendor,
	});

	const renderItem = useCallback(
		(item: FormatedProductPrice, index: number) => (
			<ProductCard
				{...item}
				key={item.id}
				listIndex={index}
				url={getProductListItemUrl(item)}
				priceComparisonDiscount={getPriceComparisonDiscount(item)}
				badges={badges}
				isFavoriteOverride={isFavorite(item.id)}
				onClick={() => {
					requestAnimationFrame(() => {
						captureEvent(productClickedEvent(item, currency, index));
					});
				}}
			/>
		),
		[getProductListItemUrl, getPriceComparisonDiscount, isFavorite, currency],
	);

	return (
		<div className="flex flex-col bg-gray-100 mt-6">
			{/* Desktop only: on mobile category lives in the filter drawer. */}
			<div
				className="hidden md:block mb-4 md:mb-6"
				data-testid="category-tiles-row"
			>
				<CategoryPills
					categories={filterConfig.categories}
					value={filters.category}
					onChange={(value) => setFilter("category", value)}
					placement="listing"
					categoryFacets={currentFacets?.category}
				/>
			</div>
			<div className="flex gap-8 md:pt-4">
				<div className="hidden md:block w-1/3 lg:w-1/4 h-fit sticky top-24">
					{useFiltersV2 ? (
						<ProductFilters
							filters={filters}
							config={filterConfig}
							onFilterChange={setFilter}
							onClearAll={clearAllFilters}
							facets={currentFacets}
							onTopProducerWarning={filterWarnings.manufacturer.show}
						/>
					) : (
						<ProductFiltersV1
							filters={filters}
							config={filterConfig}
							onFilterChange={setFilter}
							onTopProducerWarning={filterWarnings.manufacturer.show}
						/>
					)}
				</div>
				<div className="w-full md:w-2/3 lg:w-3/4 min-h-screen md:overflow-x-hidden">
					<div className="empty:hidden max-md:mb-4">
						<ProductListVendorNavigation
							isCartVendor={isCartVendor}
							selectedVendorId={selectedVendorId}
							previousVendor={previousVendor}
							onBackToDefault={restorePreviousSelection}
							onRestorePrevious={restorePreviousSelection}
						/>
					</div>
					<div className="md:hidden flex flex-row gap-2 mb-4">
						<DeliveryVendorFilter
							onPharmacySelectorOpenChange={setIsPharmacySelectorOpen}
						/>
					</div>

					<ProductList
						title={vendor?.name ?? ""}
						products={products}
						renderItem={renderItem}
						count={count}
						initialTotalCount={totalCount}
						filters={filters}
						filtersConfig={filterConfig}
						onFiltersChange={setBulkFilters}
						onFilterRemove={(category, value) => {
							removeFilter(category, value);
						}}
						onClearAllFilters={() => {
							clearAllFilters();
						}}
						useFiltersV2={useFiltersV2}
						facets={currentFacets}
						onTopProducerRemoved={filterWarnings.manufacturer.show}
						hideMobileFilterSortControls={isPharmacySelectorOpen}
					/>
				</div>
			</div>

			{(filterWarnings.manufacturer.isVisible ||
				filterWarnings.pharmacy.isVisible) && (
				<div className="fixed z-50 w-[358px] top-[96px] right-8 space-y-3 max-md:top-auto max-md:bottom-[86px] max-md:left-4 max-md:right-4 max-md:w-auto">
					{filterWarnings.manufacturer.isVisible && (
						<Snackbar
							message={t("filters.warning.manufacturer.message")}
							actionLabel={t("filters.warning.undo")}
							dismissAriaLabel={t("filters.warning.dismiss")}
							onAction={filterWarnings.manufacturer.undo}
							onDismiss={filterWarnings.manufacturer.dismiss}
						/>
					)}

					{filterWarnings.pharmacy.isVisible && (
						<Snackbar
							message={t("filters.warning.pharmacy.message")}
							actionLabel={t("filters.warning.pharmacy.cta")}
							dismissAriaLabel={t("filters.warning.dismiss")}
							onAction={filterWarnings.pharmacy.undo}
							onDismiss={filterWarnings.pharmacy.dismiss}
							onMouseEnter={filterWarnings.pharmacy.pauseAutoDismiss}
							onMouseLeave={filterWarnings.pharmacy.resumeAutoDismiss}
						/>
					)}
				</div>
			)}
		</div>
	);
}
